import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Alert,
  useTheme,
  TablePagination,
} from "@mui/material";
import {
  AddOutlined,
  CheckCircleOutline,
  RefreshOutlined,
  VerifiedUserOutlined,
  AccountBalanceOutlined,
  DateRangeOutlined,
  PercentOutlined,
  PlayArrowOutlined,
  PaymentOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import Swal from "sweetalert2";
import { formatKES } from "../WalletManagement/WalletBalance";
import {
  walletQuery,
  walletMutate,
  ROYALTY_SCHEDULES_QUERY,
  ACTIVE_ROYALTY_SCHEDULE_QUERY,
  ROYALTY_ACCRUALS_QUERY,
  CREATE_ROYALTY_SCHEDULE_MUTATION,
  APPROVE_ROYALTY_SCHEDULE_MUTATION,
  ACCRUE_ROYALTY_MUTATION,
  DEDUCT_ROYALTY_MUTATION,
} from "../../data/WalletService/walletGraphQL";
import { companyManagementUrl } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { loadUsersMap, getUserDisplayName } from "./userResolver";

const RoyaltyConfigManagement = ({ tenants = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // User Display Names Map
  const [userMap, setUserMap] = useState(new Map());

  useEffect(() => {
    let mounted = true;
    loadUsersMap().then((map) => {
      if (mounted && map) setUserMap(new Map(map));
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Tenant List (uses prop or self-heals by fetching live companies, filtering only active like MCB Switch)
  // Tenant List (filters and shows all active companies and tenants like MCB Switch)
  const [tenantList, setTenantList] = useState([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetchJsonDynamic(
          "companies",
          null,
          null,
          companyManagementUrl.uri
        );

        let companies = [];
        if (response?.results) {
          try {
            companies = JSON.parse(response.results) || [];
          } catch (e) {
            console.error("Error parsing company data:", e);
          }
        } else if (Array.isArray(response)) {
          companies = response;
        }

        // Filter active companies and sort by most recent (highest Id first)
        const activeCompanies = Array.isArray(companies)
          ? companies
              .filter((c) => c?.IsActive || c?.isActive)
              .sort((a, b) => (b?.Id || b?.id || 0) - (a?.Id || a?.id || 0))
          : [];

        const mapped = activeCompanies.map((c) => ({
          id: c.Id ?? c.id,
          name: c.CompanyName ?? c.companyName ?? c.Name ?? c.name ?? `Company #${c.Id ?? c.id}`,
          isActive: true,
        }));

        setTenantList(mapped);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  // Also reconcile if parent passes active tenants prop
  useEffect(() => {
    if (Array.isArray(tenants) && tenants.length > 0) {
      const active = tenants
        .filter((t) => t?.isActive !== false && t?.IsActive !== false)
        .map((t) => ({
          id: t.id ?? t.companyId ?? t.Id,
          name: t.name ?? t.companyName ?? t.CompanyName ?? `Tenant #${t.id ?? t.companyId ?? t.Id}`,
          isActive: true,
        }))
        .sort((a, b) => (b?.id || 0) - (a?.id || 0));
      if (active.length > 0) {
        setTenantList(active);
      }
    }
  }, [tenants]);

  const displayTenants = tenantList;

  const getCompanyName = useCallback(
    (companyId) => {
      if (!companyId && companyId !== 0) return "-";
      const cid = String(companyId);
      const match =
        displayTenants.find((t) => String(t.id ?? t.companyId) === cid) ||
        (Array.isArray(tenants) &&
          tenants.find((t) => String(t.id ?? t.companyId) === cid));
      return match ? (match.name ?? match.companyName) : `Company #${companyId}`;
    },
    [displayTenants, tenants]
  );

  // Filter State
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [accruals, setAccruals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accrualsLoading, setAccrualsLoading] = useState(false);

  // Pagination States
  const [schedulesPage, setSchedulesPage] = useState(0);
  const [schedulesRowsPerPage, setSchedulesRowsPerPage] = useState(5);
  const [accrualsPage, setAccrualsPage] = useState(0);
  const [accrualsRowsPerPage, setAccrualsRowsPerPage] = useState(5);

  // Create Modal State
  const [createOpen, setCreateOpen] = useState(false);
  const [targetCompanyId, setTargetCompanyId] = useState("");
  const [royaltyPct, setRoyaltyPct] = useState("5.0");
  const [periodType, setPeriodType] = useState("MONTHLY");
  const [revenueBasis, setRevenueBasis] = useState("DISBURSEMENTS");
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [effectiveTo, setEffectiveTo] = useState("");
  const [contractReference, setContractReference] = useState("");
  const [creating, setCreating] = useState(false);

  // Manual Accrue Modal State
  const [accrueOpen, setAccrueOpen] = useState(false);
  const [accrueCompanyId, setAccrueCompanyId] = useState("");
  const [periodStart, setPeriodStart] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .slice(0, 10)
  );
  const [periodEnd, setPeriodEnd] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [accruing, setAccruing] = useState(false);

  // Load Schedules & Accruals
  const loadData = useCallback(async () => {
    setLoading(true);
    setAccrualsLoading(true);
    const cid = selectedCompanyId ? parseInt(selectedCompanyId, 10) : null;
    try {
      const [schedRes, activeRes, accRes] = await Promise.allSettled([
        walletQuery(ROYALTY_SCHEDULES_QUERY, { companyId: cid }),
        walletQuery(ACTIVE_ROYALTY_SCHEDULE_QUERY, { companyId: cid }),
        walletQuery(ROYALTY_ACCRUALS_QUERY, { companyId: cid }),
      ]);

      if (schedRes.status === "fulfilled" && schedRes.value?.royaltySchedules) {
        setSchedules(schedRes.value.royaltySchedules);
      } else {
        setSchedules([]);
      }

      if (activeRes.status === "fulfilled" && activeRes.value?.activeRoyaltySchedule) {
        setActiveSchedule(activeRes.value.activeRoyaltySchedule);
      } else {
        setActiveSchedule(null);
      }

      if (accRes.status === "fulfilled" && accRes.value?.royaltyAccruals) {
        setAccruals(accRes.value.royaltyAccruals);
      } else {
        setAccruals([]);
      }
    } catch (err) {
      console.error("[RoyaltyConfig] Load error:", err);
    } finally {
      setLoading(false);
      setAccrualsLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create Royalty Schedule
  const handleCreateSchedule = async (e) => {
    if (e) e.preventDefault();
    if (!targetCompanyId) {
      Swal.fire("Validation Error", "Please select a target tenant company.", "warning");
      return;
    }
    const pct = parseFloat(royaltyPct);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      Swal.fire("Validation Error", "Royalty percentage must be between 0% and 100%.", "warning");
      return;
    }

    setCreating(true);
    try {
      const input = {
        companyId: parseInt(targetCompanyId, 10),
        royaltyPct: pct,
        periodType,
        revenueBasis,
        effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : new Date().toISOString(),
        effectiveTo: effectiveTo ? new Date(effectiveTo).toISOString() : null,
        contractReference: contractReference || `SAB-ROY-${targetCompanyId}-${new Date().getFullYear()}`,
      };

      const res = await walletMutate(CREATE_ROYALTY_SCHEDULE_MUTATION, { input });
      const newId = res?.createRoyaltySchedule;

      Swal.fire({
        icon: "success",
        title: "Royalty Policy Created (Draft)",
        text: `Royalty Schedule #${newId} for ${getCompanyName(targetCompanyId)} (${pct}% of revenue ${periodType.toLowerCase()}) created. Awaiting checker approval.`,
      });

      setCreateOpen(false);
      setRoyaltyPct("5.0");
      setContractReference("");
      // Switch the filter to the target company so the new schedule is visible
      setSelectedCompanyId(String(parseInt(targetCompanyId, 10)));
      // loadData() will fire automatically via the [selectedCompanyId] dep in useEffect / useCallback
      // But if it was already the same value, call explicitly:
      loadData();
    } catch (err) {
      console.error("[RoyaltyConfig] Create error:", err);
      Swal.fire("Creation Failed", err.message || "Failed to create royalty schedule.", "error");
    } finally {
      setCreating(false);
    }
  };

  // Approve Royalty Schedule (Maker-checker enforced)
  const handleApproveSchedule = async (scheduleId, companyId) => {
    const result = await Swal.fire({
      title: "Approve Royalty Policy?",
      text: `Approve Royalty Schedule #${scheduleId} for ${getCompanyName(companyId)}? This will become the ACTIVE policy and supersede any previous royalty rule. (Maker ≠ Checker enforced).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#22bf72",
      confirmButtonText: "Approve Policy",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(APPROVE_ROYALTY_SCHEDULE_MUTATION, { scheduleId: parseInt(scheduleId, 10) });
        Swal.fire("Approved & Activated", "Royalty schedule is now ACTIVE.", "success");
        loadData();
      } catch (err) {
        Swal.fire("Approval Failed", err.message || "Maker-checker policy rejected approval.", "error");
      }
    }
  };

  // Run Manual Period Accrual
  const handleRunAccrual = async () => {
    if (!accrueCompanyId) {
      Swal.fire("Validation Error", "Please select a company to accrue royalties for.", "warning");
      return;
    }
    setAccruing(true);
    try {
      const res = await walletMutate(ACCRUE_ROYALTY_MUTATION, {
        companyId: parseInt(accrueCompanyId, 10),
        periodStart: new Date(periodStart).toISOString(),
        periodEnd: new Date(periodEnd).toISOString(),
      });

      const accrualId = res?.accrueRoyalty;
      if (accrualId && accrualId > 0) {
        Swal.fire({
          icon: "success",
          title: "Royalty Accrued",
          text: `Accrual #${accrualId} calculated successfully for the period.`,
        });
      } else {
        Swal.fire({
          icon: "info",
          title: "No Accrual Generated",
          text: "No eligible revenue or no active royalty policy found for the specified period.",
        });
      }
      setAccrueOpen(false);
      loadData();
    } catch (err) {
      Swal.fire("Accrual Failed", err.message || "Failed to execute royalty accrual.", "error");
    } finally {
      setAccruing(false);
    }
  };

  // Deduct Royalty from Wallet
  const handleDeductRoyalty = async (accrualId) => {
    const result = await Swal.fire({
      title: "Deduct Royalty from Wallet?",
      text: `Debiting accrual #${accrualId} directly from company wallet and posting to platform royalty GL revenue. Continue?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1766A2",
      confirmButtonText: "Yes, Deduct Royalty",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(DEDUCT_ROYALTY_MUTATION, { accrualId: parseInt(accrualId, 10) });
        Swal.fire("Deducted", "Royalty has been debited and posted to GL.", "success");
        loadData();
      } catch (err) {
        Swal.fire("Deduction Failed", err.message || "Failed to deduct royalty.", "error");
      }
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "ACTIVE":
        return <Chip label="ACTIVE" color="success" size="small" sx={{ fontWeight: 700 }} />;
      case "PENDING_APPROVAL":
        return <Chip label="PENDING APPROVAL" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case "DRAFT":
        return <Chip label="DRAFT" color="info" size="small" sx={{ fontWeight: 700 }} />;
      case "SUPERSEDED":
        return <Chip label="SUPERSEDED" color="default" size="small" sx={{ fontWeight: 700 }} />;
      default:
        return <Chip label={status || "UNKNOWN"} size="small" />;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── Banner: Distinct Royalty Engine ──────────────────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          background: isDark
            ? "linear-gradient(135deg, rgba(156,39,176,0.18) 0%, rgba(23,102,162,0.15) 100%)"
            : "linear-gradient(135deg, #faf5ff 0%, #f0f7ff 100%)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <AccountBalanceOutlined sx={{ color: "#9c27b0", fontSize: 26 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                  Tenant Royalty Configuration & Period Revenue Share
                </Typography>
                {activeSchedule ? (
                  <Chip
                    icon={<CheckCircleOutline fontSize="small" />}
                    label={`Active Policy #${activeSchedule.id}: ${activeSchedule.royaltyPct}% (${activeSchedule.periodType})`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                ) : (
                  <Chip label="No Policy Selected" color="default" size="small" sx={{ fontWeight: 700 }} />
                )}
              </Stack>
              <Typography variant="body2" sx={{ color: colors.grey[400], maxWidth: 850 }}>
                Unlike tiered transaction tariffs, <strong>Royalty is a percentage of revenue depending on a specific period</strong>{" "}
                (e.g., monthly percentage of revenue, quarterly, or annual). Royalty accruals are calculated over the period
                and debited from tenant wallets with maker-checker approvals and automated GL journal balancing.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PlayArrowOutlined />}
                onClick={() => setAccrueOpen(true)}
                sx={{
                  borderColor: "#9c27b0",
                  color: isDark ? "#ce93d8" : "#9c27b0",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2,
                }}
              >
                Run Manual Accrual
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  backgroundColor: "#9c27b0",
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2.5,
                  borderRadius: 2,
                }}
              >
                Create Royalty Policy
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* ── Filter Bar ───────────────────────────────────────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              label="Filter by Tenant Institution"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">All Tenant Institutions</MenuItem>
              {displayTenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} (#{t.id})
                </MenuItem>
              ))}
            </TextField>

            <Tooltip title="Refresh royalty policies and accruals">
              <IconButton size="small" onClick={loadData}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Royalty Schedules Table ──────────────────────────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mb: 0.5 }}>
            Configured Royalty Schedules & Approvals
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mb: 2 }}>
            Schedules define the contract revenue percentage per period. Maker-checker policy enforces creator &ne; approver.
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : schedules.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No royalty schedules configured. Click &quot;Create Royalty Policy&quot; to define a revenue share rule.
              </Typography>
            </Box>
          ) : (<>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tenant Company</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#9c27b0" }}>Royalty Rate (%)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Period Frequency</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Revenue Basis</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Effective Period</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Approved By</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules
                    .slice(
                      schedulesPage * schedulesRowsPerPage,
                      schedulesPage * schedulesRowsPerPage + schedulesRowsPerPage
                    )
                    .map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>#{row.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getCompanyName(row.companyId)}
                        </Typography>
                        {row.contractReference && (
                          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block" }}>
                            Ref: {row.contractReference}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#9c27b0" }}>
                        {row.royaltyPct}% of revenue
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.periodType}
                          size="small"
                          sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {row.revenueBasis || "DISBURSEMENTS"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>
                        {row.effectiveFrom ? new Date(row.effectiveFrom).toLocaleDateString() : "Immediate"}
                        {row.effectiveTo ? ` to ${new Date(row.effectiveTo).toLocaleDateString()}` : " (Indefinite)"}
                      </TableCell>
                      <TableCell>{getStatusChip(row.status)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                          {getUserDisplayName(row.createdBy, userMap)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          {row.createdDate ? new Date(row.createdDate).toLocaleDateString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.approvedBy ? (
                          <>
                            <Typography variant="caption" sx={{ display: "block", color: "#22bf72", fontWeight: 700 }}>
                              {getUserDisplayName(row.approvedBy, userMap)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                              {row.approvedDate ? new Date(row.approvedDate).toLocaleDateString() : ""}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.status === "DRAFT" || row.status === "PENDING_APPROVAL" ? (
                          <Tooltip title="Approve Royalty Policy (Checker)">
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<VerifiedUserOutlined />}
                              onClick={() => handleApproveSchedule(row.id, row.companyId)}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              Approve
                            </Button>
                          </Tooltip>
                        ) : (
                          <Chip label="Approved" size="small" variant="outlined" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={schedules.length}
              page={schedulesPage}
              onPageChange={(_, newPage) => setSchedulesPage(newPage)}
              rowsPerPage={schedulesRowsPerPage}
              onRowsPerPageChange={(e) => {
                setSchedulesRowsPerPage(parseInt(e.target.value, 10));
                setSchedulesPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${
                  isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                }`,
              }}
            />
          </>)}
        </CardContent>
      </Card>

      {/* ── Royalty Accruals Ledger ──────────────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                Period Royalty Accrual Ledger & Settlements
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Periodic ledger records of revenue accumulated, royalty amount owed, and wallet deduction status.
              </Typography>
            </Box>
            <Tooltip title="Reload accruals">
              <IconButton size="small" onClick={loadData}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {accrualsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : accruals.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No royalty accruals found for this period. Click &quot;Run Manual Accrual&quot; to calculate the current month&apos;s dues.
              </Typography>
            </Box>
          ) : (<>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Accrual ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Billing Period Range</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total Revenue</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#9c27b0" }}>Royalty Owed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Deduction</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accruals
                    .slice(
                      accrualsPage * accrualsRowsPerPage,
                      accrualsPage * accrualsRowsPerPage + accrualsRowsPerPage
                    )
                    .map((acc) => (
                    <TableRow key={acc.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>#{acc.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getCompanyName(acc.companyId)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(acc.periodStart).toLocaleDateString()} &ndash;{" "}
                        {new Date(acc.periodEnd).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">{formatKES(acc.revenueAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#9c27b0" }}>
                        {formatKES(acc.royaltyAmount)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={acc.status || "ACCRUED"}
                          size="small"
                          color={acc.status === "DEDUCTED" ? "success" : "warning"}
                          sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        {acc.deductedFromWallet ? (
                          <Typography variant="caption" sx={{ color: "#22bf72", fontWeight: 700 }}>
                            Debited ({new Date(acc.deductedDate || acc.createdDate).toLocaleDateString()})
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: "#ed6c02", fontWeight: 700 }}>
                            Pending Wallet Debit
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!acc.deductedFromWallet && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PaymentOutlined />}
                            onClick={() => handleDeductRoyalty(acc.id)}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Deduct
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={accruals.length}
              page={accrualsPage}
              onPageChange={(_, newPage) => setAccrualsPage(newPage)}
              rowsPerPage={accrualsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setAccrualsRowsPerPage(parseInt(e.target.value, 10));
                setAccrualsPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${
                  isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                }`,
              }}
            />
          </>)}
        </CardContent>
      </Card>

      {/* ── Modal: Create Royalty Policy ─────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Configure Royalty Policy (% of Revenue per Period)
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            Set percentage of revenue per month/quarter for tenant contract royalty.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Target Tenant Institution *"
                value={targetCompanyId}
                onChange={(e) => setTargetCompanyId(e.target.value)}
              >
                {displayTenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} (#{t.id})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Royalty Percentage (%) *"
                value={royaltyPct}
                onChange={(e) => setRoyaltyPct(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="e.g. 5.0% of total revenue per period"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Period Frequency *"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
              >
                <MenuItem value="MONTHLY">MONTHLY (Every Month)</MenuItem>
                <MenuItem value="QUARTERLY">QUARTERLY (Every 3 Months)</MenuItem>
                <MenuItem value="ANNUAL">ANNUAL (Yearly)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Revenue Basis *"
                value={revenueBasis}
                onChange={(e) => setRevenueBasis(e.target.value)}
              >
                <MenuItem value="DISBURSEMENTS">DISBURSEMENTS (Loan Volume)</MenuItem>
                <MenuItem value="COLLECTIONS">COLLECTIONS (Repayment Volume)</MenuItem>
                <MenuItem value="NET_INCOME">NET_INCOME (Interest Margin)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Contract / SLA Reference"
                placeholder="e.g. SAB-2026-MOU-01"
                value={contractReference}
                onChange={(e) => setContractReference(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Effective From Date"
                InputLabelProps={{ shrink: true }}
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Effective To Date (Optional)"
                InputLabelProps={{ shrink: true }}
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
                helperText="Leave empty for indefinite policy"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSchedule}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <CheckCircleOutline />}
            sx={{
              backgroundColor: "#9c27b0",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
            }}
          >
            {creating ? "Saving..." : "Create Royalty Policy (Draft)"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal: Manual Accrual Trigger ────────────────────────────────── */}
      <Dialog open={accrueOpen} onClose={() => setAccrueOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Run Manual Royalty Accrual
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            Calculate and record royalty due for a specific period.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Tenant Institution *"
              value={accrueCompanyId}
              onChange={(e) => setAccrueCompanyId(e.target.value)}
            >
              {displayTenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} (#{t.id})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Period Start Date *"
              InputLabelProps={{ shrink: true }}
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Period End Date *"
              InputLabelProps={{ shrink: true }}
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setAccrueOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRunAccrual}
            disabled={accruing}
            startIcon={accruing ? <CircularProgress size={16} /> : <PlayArrowOutlined />}
            sx={{
              backgroundColor: "#9c27b0",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              px: 2.5,
            }}
          >
            {accruing ? "Calculating..." : "Calculate & Accrue"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoyaltyConfigManagement;
